(ns hai.ui.components.form.select.components
  (:require
   [clojure.string :as str]
   [hai.ui.components :as c]
   [hai.ui.components.form.select.common :as common]
   [hai.ui.components.form.select.multi-select :as multi-select]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defsub]]
   [hai.ui.tokens :as t]
   [humbleai.belt.dom :as dom]
   [humbleai.belt.vector :refer [toggle-membership]]
   [lambdaisland.ornament :as o :refer [defstyled]]
   [reagent.core :as r]))

(defsub :multi-select/selected
  [?id
   selected <- [:form.field/current-value ?id :multi-select/selected]]
  selected)

(defsub :multi-select/selected?
  [?id
   selected <- [:form.field/current-value ?id :multi-select/selected]]
  (seq selected))

(defstyled pill :li
  :whitespace-nowrap
  :inline-flex
  :items-center
  :gap-1
  :rounded
  :px-6px
  [:& [:.remove-token :inline :cursor-pointer]]
  ([{{{:form.field/keys [set-value! current-value]} :multi-select/selected} :form/fields}
    option
    content]
   (let [close-on-click (fn [_] (set-value! (toggle-membership current-value option)))
         bg-color "white"]
     [:<>
      {:style {:background-color bg-color}}
      content
      [:span.remove-token
       {:on-click close-on-click}
       [icons/filled-highlight_off]]])))

(defstyled pill-radix :li
  :whitespace-nowrap
  :inline-flex
  :items-center
  :gap-1
  :rounded
  :px-6px
  [:& [:.remove-token :inline :cursor-pointer
       {:fill "var(--crimson-11)"}]]
  ([{:as select
     {{:form.field/keys [set-value! current-value]} :multi-select/selected} :form/fields
     {label-render :select.render/label} :select/render}
    option]
   [:<>
    [multi-select/pill-close
     select
     option
     [:span.remove-token [icons/filled-highlight_off]]]
    [c/Text [label-render option]]]))

(defsub :multi-select.option/selected?
  [?id
   option
   selected <- [:multi-select/selected ?id]]
  (<= 0 (.indexOf selected option)))

(defstyled pills* :ul
  :flex
  :flex-row
  :flex-wrap
  :gap-1
  ([select]
   [multi-select/pills-core select]))

(defn pills
  [select]
  (r/with-let [selected? (<s [:multi-select/selected? select])]
    (when @selected?
      [pills* select])))

(defstyled options :div
  common/options-styled
  ([{:as select
     {{:form.field/keys [current-value]} :table/rows} :form/fields}]
   (r/with-let [show-options? (<s [:select/show-options? select])]
     (when @show-options?
       (into [:<>]
             (map (partial multi-select/option-core select))
             current-value)))))

(defstyled option :span
  common/option-styled
  ([select option content]
   (let [selected? (<s [:multi-select.option/selected? select option])]
     (fn [select option content]
       [:<> {:class (cond-> [] @selected? (conj "active"))
             :tabindex 0}
        content]))))

(defstyled options-radix :div
  [:#options-radix-container>* {:z-index "1 !important"}]
  ([{:as select
     {{:form.field/keys [current-value]} :table/rows
      {search :form.field/current-value} :table/search} :form/fields}]
   (r/with-let [show-options? (<s [:select/show-options? select])]
     [:<>
      ;; TODO using a popover, radix does not have a combobox, no effort has been made to make this
      ;; accessible. AriaKit seems to have the best combobox.
      [c/PopoverRoot
       ;; A suggestions popover is only relevant while there's a query. Gating on a
       ;; non-empty search (not just show-options? + rows) means clearing the box --
       ;; whether by picking a contact, clicking Share, or deleting the text -- always
       ;; closes it. Clearing the field remounts the auto-focused input, whose on-focus
       ;; re-fires `:select/show-options`; without this guard that refocus, landing after
       ;; the click-outside that closed it, would pop the dropdown back open over stale rows.
       {:open (and @show-options?
                   (boolean (seq current-value))
                   (boolean (not-empty search)))}
       [c/PopoverTrigger [:div]]
       [c/PopoverContent
        ;; TODO dynamic class name for container
        {;:container (.getElementById js/document "options-radix-container") ;; FIXME: hack for GG-1793
         :onInteractOutside (fn [_] (>e [:select/hide-options select]))
         :onOpenAutoFocus dom/stop
         :side "bottom"
         :sideOffset 24}
        ;; PopoverContent portals to <body>, so this component's scoped
        ;; `.content` rule can't reach it — the row layout/gap must be inline
        ;; to actually apply (otherwise the rows collapse together).
        (into [:div.content
               {:style {:display "flex"
                        :flex-direction "column"
                        :gap "1rem"}}]
              (map (fn [option]
                     [multi-select/option-core select option]))
              current-value)]]
      [:div#options-radix-container]])))

(defstyled option-radix :div
  :flex :items-center
  [:.option {:color t/--text-secondary}]
  [:.active {:background-color t/--bg-2}]
  [:.disabled {:opacity 0.5 :cursor "not-allowed"}]
  ([{{label-render :select.render/label
      disabled-reason :select.render/disabled-reason} :select/render :as select}
    option]
   (let [selected? (<s [:multi-select.option/selected? select option])
         active-option? (<s [:select/active-option? select option])]
     (fn [_select option]
       ;; Non-nil reason => the option is disabled; wrap it in a tooltip that
       ;; explains why it can't be picked.
       (let [disabled-message (when disabled-reason (disabled-reason option))
             button [c/Button
                     {:size "2"
                      :class (str/join " " (cond-> ["option"]
                                             @active-option? (conj "active")
                                             disabled-message (conj "disabled")))
                      :variant "ghost"
                      :color (if @selected?
                               "iris"
                               "gray")}
                     [label-render option]]]
         (if disabled-message
           [c/Tooltip {:content disabled-message} button]
           button))))))

(o/defstyled field-with-pills-below-render :div
  :relative
  :flex :flex-col :gap-3
  ([{{options-render :select.render/options
      pills-render :multi-select.render/pills} :select/render
     :as select}]
   [:<>
    [:div
     [multi-select/select-input select]
     [options-render select]]
    [pills-render select]]))

(def radix
  {:select.render/field field-with-pills-below-render
   :select.render/option option-radix
   :select.render/options options-radix
   :multi-select.render/pill pill-radix
   :multi-select.render/pills pills})
